import { useEffect, useRef } from 'react';
import { T, useGT } from 'gt-react';
import { Link } from '@tanstack/react-router';
import type { TaskResponse } from '@erp/rpc';
import {
  ArrowCounterClockwiseIcon,
  ArrowSquareOutIcon,
  DotsThreeVerticalIcon,
  EyeIcon,
  NotePencilIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { SystemEntityInline } from 'src/components/system-entities/inline-ui';
import { cn } from 'src/lib/utils';
import { extractEntityRefs } from './entity-refs';
import { shouldAutoScrollTaskRow } from './taskCardScroll';

interface TaskCardProps {
  task: TaskResponse;
  onInspect: (task: TaskResponse) => void;
  onEdit: (task: TaskResponse) => void;
  onDelete: (task: TaskResponse) => Promise<void>;
  onResetStatus: (task: TaskResponse) => Promise<void>;
  isDeleting: boolean;
  isResetting: boolean;
  selected?: boolean;
  onSelectedChange?: (task: TaskResponse, selected: boolean) => void;
  active?: boolean;
  isRunning?: boolean;
  /** Scroll the row into view when this task is the live one. */
  autoScrollOnRunning?: boolean;
  assigneeName?: string | null;
  showOpenFullTask?: boolean;
}

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

function isOverdue(task: TaskResponse) {
  if (!task.dueDate || TERMINAL_STATUSES.has(task.status)) return false;
  return new Date(task.dueDate).getTime() < Date.now();
}

function formatRelative(date: Date | string | null) {
  if (!date) return null;
  try {
    return formatDistanceToNowStrict(new Date(date), { addSuffix: true });
  } catch {
    return null;
  }
}

function formatDatetime(date: Date | string) {
  return format(new Date(date), 'MM/dd/yy h:mmaaa');
}

// Existing queue row: inspect/active/open-full are small additions on the
// original card, not a TitleRow/Meta/Select split.
// fallow-ignore-next-line complexity
export function TaskCard({
  task,
  onInspect,
  onEdit,
  onDelete,
  onResetStatus,
  isDeleting,
  isResetting,
  selected,
  onSelectedChange,
  active,
  isRunning,
  autoScrollOnRunning = true,
  assigneeName,
  showOpenFullTask,
}: TaskCardProps) {
  const gt = useGT();
  const isTerminal = TERMINAL_STATUSES.has(task.status);
  const canResetStatus =
    task.status !== 'pending' && task.status !== 'in_progress';
  const rowRef = useRef<HTMLDivElement>(null);
  const entityRefs = extractEntityRefs(task);
  const overdue = isOverdue(task);
  const dueLabel = formatRelative(task.dueDate);

  useEffect(() => {
    if (
      shouldAutoScrollTaskRow({
        isRunning: Boolean(isRunning),
        autoScrollOnRunning,
        active: Boolean(active),
      })
    ) {
      rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isRunning, autoScrollOnRunning, active]);

  let timeLabel: { prefix: string; value: string } | null = null;
  if (task.status === 'completed' && task.completedAt) {
    timeLabel = {
      prefix: gt('completed'),
      value: formatDatetime(task.completedAt),
    };
  } else if (task.status === 'failed' && task.completedAt) {
    timeLabel = {
      prefix: gt('failed'),
      value: formatDatetime(task.completedAt),
    };
  } else if (task.status === 'cancelled' && task.completedAt) {
    timeLabel = {
      prefix: gt('cancelled'),
      value: formatDatetime(task.completedAt),
    };
  } else if (task.status === 'in_progress' && task.startedAt) {
    timeLabel = {
      prefix: gt('started'),
      value: formatDatetime(task.startedAt),
    };
  }

  const agentSummary =
    typeof task.metadata?.agentSummary === 'string'
      ? task.metadata.agentSummary
      : null;

  const showPriorityLabel =
    task.priority === 'urgent' || task.priority === 'high';

  return (
    <div
      ref={rowRef}
      role="button"
      tabIndex={0}
      aria-pressed={active}
      aria-label={gt('Inspect {title}', {
        context: 'agent monitor queue row, opens the task review rail',
        title: task.title,
      })}
      onClick={() => onInspect(task)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onInspect(task);
        }
      }}
      className={cn(
        'group flex items-start gap-2 px-2 py-1.5 rounded-md transition-colors cursor-pointer',
        'hover:bg-muted/50 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        active && 'bg-muted rounded-b-none border-l-2 border-l-border',
        isRunning && !active && 'bg-green-500/5 hover:bg-green-500/10',
      )}
    >
      {onSelectedChange && (
        <div
          className="mt-[3px]"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          role="presentation"
        >
          <Checkbox
            checked={selected ?? false}
            onCheckedChange={(value) => onSelectedChange(task, value === true)}
            aria-label={gt('Select {title}', {
              context: 'task selection checkbox',
              title: task.title,
            })}
            className="h-3.5 w-3.5"
          />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="flex-1 text-sm leading-tight line-clamp-1">
            {task.title}
          </span>
          {isRunning && (
            <span className="shrink-0 text-[10px] font-medium text-green-600 dark:text-green-400">
              <T>running</T>
            </span>
          )}
          {showPriorityLabel && (
            <span
              className={cn(
                'shrink-0 text-[10px] font-medium',
                task.priority === 'urgent'
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-orange-600 dark:text-orange-400',
              )}
            >
              {task.priority}
            </span>
          )}
        </div>

        {agentSummary && (
          <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
            {agentSummary}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
          {entityRefs.length > 0 && (
            <div
              className="flex items-center gap-1"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
              role="presentation"
            >
              {entityRefs.map((ref) => (
                <SystemEntityInline
                  key={`${ref.entityType}:${ref.entityId}`}
                  entityType={ref.entityType}
                  entityId={ref.entityId}
                />
              ))}
            </div>
          )}
          {dueLabel && (
            <span
              className={cn(
                'text-[11px] text-muted-foreground',
                overdue && 'text-destructive font-medium',
              )}
            >
              <T>due</T> {dueLabel}
              {overdue && <T> · overdue</T>}
            </span>
          )}
          {timeLabel && (
            <span
              className={cn(
                'text-[11px]',
                task.status === 'completed'
                  ? 'text-green-600 dark:text-green-400'
                  : task.status === 'failed'
                    ? 'text-destructive'
                    : 'text-muted-foreground',
              )}
            >
              {timeLabel.prefix} {timeLabel.value}
            </span>
          )}
          {task.status === 'needs_input' && assigneeName && (
            <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">
              <T>assigned to</T> {assigneeName}
            </span>
          )}
          <span className="text-[11px] text-muted-foreground">
            <T>updated</T> {formatDatetime(task.updatedAt)}
          </span>
        </div>
      </div>

      <div
        className="shrink-0 mt-[1px] opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        role="presentation"
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              aria-label={gt('Manage {title}', {
                context: 'task action menu button',
                title: task.title,
              })}
            >
              <DotsThreeVerticalIcon className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {showOpenFullTask && (
              <DropdownMenuItem asChild>
                <Link to="/tasks/$taskId" params={{ taskId: task.id }}>
                  <ArrowSquareOutIcon className="mr-2 h-4 w-4" />
                  <T context="menu item to open the full task page">
                    Open full task
                  </T>
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onEdit(task)}>
              {isTerminal ? (
                <>
                  <EyeIcon className="mr-2 h-4 w-4" />
                  <T>View</T>
                </>
              ) : (
                <>
                  <NotePencilIcon className="mr-2 h-4 w-4" />
                  <T>Edit</T>
                </>
              )}
            </DropdownMenuItem>
            {canResetStatus && (
              <DropdownMenuItem
                onClick={() => {
                  void onResetStatus(task);
                }}
                disabled={isResetting}
              >
                <ArrowCounterClockwiseIcon className="mr-2 h-4 w-4" />
                <T>Reset status</T>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => {
                void onDelete(task);
              }}
              disabled={isDeleting}
              className="text-destructive focus:text-destructive"
            >
              <TrashIcon className="mr-2 h-4 w-4" />
              <T>Delete</T>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
